import { ColumnDef, ColumnMeta, createColumnHelper } from "@tanstack/react-table";
import merge from "lodash/merge";
import { Fragment } from "react/jsx-runtime";

import { FormatDate } from "@/components/Date";
import { ErrorSpan } from "@/components/ErrorSpan";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { formatDineroCad } from "@/utils/dinero-util";
import { renderRowPropComponent, renderRowPropElement } from "@/utils/react-table-utils";
import { getCostTaxBreakdownColumnDefs } from "../CostTaxBreakdownColumns";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";
import { LandedCostsTableRow } from "./landed-cost-table-data";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";

// For receipt columns, set the rowspan of each cell on the on the first line to the total number of lines. Hide all subsequent rows for the same receipt
const receiptColCellProps: ColumnMeta<LandedCostsTableRow, unknown>["cellProps"] = ({ row }) => {
  const { receipt } = row.original;
  const lineItem = "lineItem" in row.original ? row.original.lineItem : undefined;
  const firstLineId = receipt.jobLines[0]!.id;
  const isFirstLine = lineItem?.id === firstLineId;
  return isFirstLine ?
      { rowSpan: receipt.jobLines.length + receipt.jobReceivingCosts.length }
    : { className: "d-none" };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<LandedCostsTableRow>();
const itemReceiptAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: receiptColCellProps } }, column));
const lineItemAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ enableSorting: false }, column));

const itemReceiptColumns = [
  itemReceiptAccessorCol("receipt.number", {
    header: "Item Receipt #",
    cell: ({ getValue, row }) => <SosEntityLink type="itemreceipt" id={row.original.receipt.id} label={getValue()} />,
  }),
  itemReceiptAccessorCol("receipt.date", {
    header: "Date",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  itemReceiptAccessorCol("receipt.vendor.name", { header: "Vendor", meta: { cellProps: receiptColCellProps } }),
  itemReceiptAccessorCol("receipt.vendorReceiptNum", {
    header: "Vendor #",
  }),
  itemReceiptAccessorCol("receipt.linkedPos", {
    header: "Linked POs",
    cell: ({ row, getValue }) =>
      getValue().map((po, index) => (
        <Fragment key={po.id}>
          {index > 0 && ", "}
          <JobTrackingInternalLink trackerPath="purchase-orders">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityLineItemHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="purchaseorder"
                id={po.id}
                lineNumber={0}
                label={po.number}
              />
            )}
          </JobTrackingInternalLink>
        </Fragment>
      )),
  }),
  itemReceiptAccessorCol("receipt.currency.code", {
    header: "Currency",
    cell: ({
      row: {
        original: { receipt },
      },
    }) => (
      <>
        {receipt.currency.code}
        {receipt.vendor.currency && receipt.currency.code !== receipt.vendor.currency.code && (
          <ErrorSpan>⚠ Mismatch With Vendor</ErrorSpan>
        )}
      </>
    ),
  }),
  itemReceiptAccessorCol("receipt.exchangeRate", {
    header: "Exchange Rate",
  }),
  itemReceiptAccessorCol("receipt.jobLinesSummary.categorizedCosts.material.subtotal", {
    header: "Material Costs",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  itemReceiptAccessorCol("receipt.jobLinesSummary.categorizedCosts.nonMaterial.subtotal", {
    header: "Non-Material Costs",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  itemReceiptAccessorCol("receipt.jobLinesSummary.categorizedCosts.shipping.subtotal", {
    header: "Shipping Costs",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  ...getCostTaxBreakdownColumnDefs<LandedCostsTableRow>(
    (row) => row.receipt.jobLinesSummary.cost,
    "receipt_jobLinesSummary_cost",
    { meta: { cellProps: receiptColCellProps } },
  ),
];

const lineItemColumns = [
  lineItemAccessorCol("normalizedLine.lineNumber", {
    header: "Line #",
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: { className: "border-start-double" },
    },
  }),
  lineItemAccessorCol("normalizedLine.item.name", {
    header: "Item",
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.normalizedLine.item.id}
            label={row.original.normalizedLine.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  lineItemAccessorCol("normalizedLine.item.type", { header: "Type" }),
  lineItemAccessorCol("normalizedLine.description", {
    header: "Description",
    cell: renderRowPropElement((row) => row.normalizedLine.descriptionElement),
  }),
  lineItemAccessorCol("normalizedLine.uomQuantity", {
    header: "Quantity",
    cell: renderRowPropElement((row) => row.normalizedLine.uomQuantityElement),
  }),
  lineItemAccessorCol("normalizedLine.unitCost", {
    header: "Unit Cost",
    cell: renderRowPropElement((row) => row.normalizedLine.unitCostElement),
  }),
  ...getCostTaxBreakdownColumnDefs<LandedCostsTableRow>(
    (row) => row.normalizedLine.totalCost,
    "normalizedLine_totalCost",
  ),
  lineItemAccessorCol("normalizedLine.bin.number", {
    header: "To Bin",
    cell: renderRowPropComponent((row) => row.normalizedLine.BinComponent),
  }),
  lineItemAccessorCol((row) => row.normalizedLine.linkedPo?.number, {
    id: "normalizedLine_linkedPo_number",
    header: "Linked PO",
    cell: renderRowPropComponent((row) => row.normalizedLine.linkedPoComponent),
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const landedCostsTableColumns = [
  columnHelper.group({ header: "Item Receipts", columns: itemReceiptColumns }),
  columnHelper.group({
    header: "Line Items",
    columns: lineItemColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
] as const satisfies ColumnDef<LandedCostsTableRow>[];
